// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll as hideAllTippyInstances, Instance as TippyInstance } from "tippy.js";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Clickable, { ClickablePresentationProps, ClickableProps, StyledButton } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo, IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import { LazyImage } from "@insite/mobius/index";
import { LazyImageStyle } from "@insite/mobius/LazyImage";
import * as ariaAppHider from "@insite/mobius/Overlay/helpers/ariaAppHider";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import FancyScroll from "@stanion/App/Components/Common/FancyScroll";
import LazyTippy from "@stanion/App/Components/Common/LazyTippy";
import { xHeaderUpdate } from "@stanion/App/Handlers/XNG/Header";
import { addScrollbarGutter, color, ellipsis, removeScrollbarGutter, styledScrollbar } from "@stanion/XDL/Utilities";
import { FontWeightProperty } from "csstype";
import isMobile from "ismobilejs";
import React, { RefObject } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { ThemeConsumer, ThemeProps, withTheme } from "styled-components";

export interface MappedLink {
    // expanded from content-library/src/widgets/header/MainNavigation.tsx
    title: string;
    url?: string;
    type?: string;
    image?: string;
    clickableProps?: ClickableProps;
    openInNewWindow?: boolean;
    numberOfColumns?: number;
    maxDepth?: number;
    childrenType?: "MegaMenu" | "CascadingMenu";
    children?: MappedLink[];
    excludeFromNavigation?: boolean;
    iconProps?: IconPresentationProps;
    /**
     * XNG-Changes:
     * onClick callback method
     */
    onClick?: () => void;
    /**
     * XNG-Changes: return Category isFeatured (OOTB value)
     */
    isFeatured?: boolean;
}

export interface MenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<MenuPresentationProps>;
        menu?: StyledProp<MenuPresentationProps>;
        menuItem?: StyledProp<MenuPresentationProps>;
    };
    /** Props passed to the menu items.
     * @themable */
    menuItemTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu item Clickable.
     * @themable */
    menuItemClickableProps?: ClickablePresentationProps;
    /** Props to be passed to the right chevron arrow for menu items with children.
     * @themable */
    moreIconProps?: IconPresentationProps;
}

type MenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /** Maximum number of menus that will appear through submenu cascading. */
        maxDepth?: number;
        /** The nested child structure that will be rendered by the menu. */
        menuItems: MappedLink[];
        /** The element that triggers the menu. */
        menuTrigger: React.ReactElement;
        /** Width of the menu and submenus in pixels. */
        width?: number;
        /** Allows forcing the state of this menu to be open */
        isOpen?: true;
        /** Session boolean value that decides whether to show Change Customer Link */
        displayChangeCustomerLink?: boolean;
        /** XNG-Changes: tippy trigger prop */
        tippyTrigger?: string;
        /** XNG-Changes: To prevent Menu close by click on trigger */
        hideOnClick?: boolean;
    }
>;

// XNG-Changes
const mapDispatchToProps = {
    xHeaderUpdate,
};

/** MenuComponentProps with defaulted properties now listed as not-optional, plus theme. */
export type MenuProps = MenuComponentProps &
    MenuPresentationProps &
    ThemeProps<BaseTheme> &
    ResolveThunks<typeof mapDispatchToProps>;

type MenuStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        menuHeight?: number;
    };

// eslint-disable-next-line no-unexpected-multiline
const MenuStyle = styled.div<MenuStyleProps>`
    background: ${color("common.white")};
    width: ${getProp("width", 175)}px;
    max-width: 25vw;
    min-height: 100px;
    height: 600px;
    max-height: calc(100vh - 280px);
    padding: 16px 0;
    list-style: none;
    margin: 0;
    overflow: auto;
    box-shadow: -1px 0 0 0 ${color("common.border")};

    ${styledScrollbar(6)}
    ${injectCss}
`;

type MenuWrapperProps = { canBeOpen?: boolean; isOpen?: true } & InjectableCss;

const MenuWrapper = styled.nav<MenuWrapperProps>`
    ${injectCss}
`;

const MenuItemText = styled(Typography as any)<{ hasChildren?: boolean; weight?: FontWeightProperty }>`
    color: ${getColor("text")};
    display: inline;
    box-sizing: border-box;
    font-size: 12px;
    letter-spacing: 0;
    line-height: 13px;
    ${ellipsis(2)}
`;

const menuItemColorStyle = {
    default: {
        background: "transparent",
        text: "text.main",
        icon: "primary",
    },
    active: {
        background: "common.hover",
        text: "common.white",
        icon: "common.white",
    },
};

// eslint-disable-next-line no-unexpected-multiline
type MenuItemStyleProps = ThemeProps<BaseTheme> &
    InjectableCss & {
        width?: number;
        onClick: () => void;
    };

const MenuItemWrapper = styled.nav<MenuWrapperProps>`
    transition: all cubic-bezier(0, 0, 0.5, 1) 300ms;

    &:focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:focus > ${MenuStyle}, &:hover > ${MenuStyle}, &.focus-within > ${MenuStyle} {
        display: block;
        left: ${getProp("width", 175)}px;
        top: 0;
    }

    &:hover {
        background: ${color(menuItemColorStyle.active.background)};
    }

    &[class*="depth-"]:hover {
        & > li > a > ${IconWrapper} {
            color: ${color(menuItemColorStyle.active.icon)};
        }
        & > li > a > ${MenuItemText} {
            color: ${color(menuItemColorStyle.active.text)};
        }
    }
`;

const MenuItemStyle = styled.li<MenuItemStyleProps>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;

    ${StyledButton} {
        display: inline-block;
        width: calc(100% - 15px);
        padding: 8px 16px;
    }

    ${IconWrapper} {
        top: 8px;
        height: 20px;
        width: 20px;
    }

    ${TypographyStyle} {
        font-size: 14px;
        line-height: 22px;
    }

    ${LazyImageStyle} {
        float: right;
        margin-left: auto;
        img {
            width: 85px;
            height: 25px;
            object-fit: contain;
        }
    }

    ${injectCss}
`;

/**
 * XNG-Changes
 */
const hideOnPopperBlurPlugin = {
    name: "hideOnPopperBlur",
    defaultValue: true,
    fn(instance: TippyInstance) {
        return {
            onCreate() {
                instance.popper.addEventListener("focusout", event => {
                    if (
                        event.relatedTarget &&
                        event.relatedTarget instanceof HTMLElement &&
                        !instance.popper.contains(event.relatedTarget)
                    ) {
                        instance.hide();
                    }
                });
            },
        };
    },
};

const trigger = React.createRef<HTMLElement>();

interface MenuState {
    canBeOpen: boolean;
    isMobile: boolean;
    shouldRender: boolean;
}

/**
 * A cascading menu that renders a defined number of sub-menus.
 */
class Menu extends React.Component<MenuProps, MenuState> {
    menuWrapperRef = React.createRef<HTMLElement>();

    static defaultProps: Partial<MenuProps> = {
        maxDepth: 3,
        width: 444,
        tippyTrigger: "mouseenter focusin",
        hideOnClick: false,
    };

    state: MenuState = {
        canBeOpen: true,
        isMobile: false,
        shouldRender: true,
    };

    componentDidMount() {
        this.setState({
            isMobile: isMobile().any,
        });

        window.addEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    componentWillUnmount() {
        window.removeEventListener("resize", () => {
            window.setTimeout(() => {
                this.setState({
                    isMobile: isMobile().any,
                });
            }, 50);
        });
    }

    closeMenu = () => {
        // On Mobile, the below logic
        // where we switch the canBeOpen state from false to true
        // causes the mobile experience to not function well
        // due to Mobile not doing hover functionality

        // Solution:
        // I prevent the boolean toggle when we detect mobile

        this.setState({ canBeOpen: this.state.isMobile }, () =>
            setTimeout(() => {
                // The make the all browsers act the same on item click.
                if (document.activeElement instanceof HTMLElement) {
                    document.activeElement.blur();
                }
                this.setState({ canBeOpen: true });
            }, 100),
        );
    };

    closeMenuWithoutChildren = (hasMenuChildren?: boolean) => {
        // This is for high resolution mobile users.
        // So that the menu closes on nav items without children
        if (!hasMenuChildren) {
            this.setState(
                {
                    shouldRender: false,
                },
                () => {
                    this.setState({ shouldRender: true });
                },
            );
        }
    };

    menuWrapperMouseLeaveHandler = () => {
        if (!this.menuWrapperRef.current) {
            return;
        }

        this.menuWrapperRef.current.blur();

        const menuTrigger = this.menuWrapperRef.current.children[0] as HTMLElement;
        if (!menuTrigger) {
            return;
        }

        menuTrigger.blur();
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const { menuItems, menuTrigger, isOpen, mergeCss, ...otherProps } = this.props;
                    const { applyProp, spreadProps, applyStyledProp } = applyPropBuilder(
                        { ...otherProps, theme },
                        { component: "menu" },
                    );
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.menu?.defaultProps?.mergeCss;
                    const renderMenuItems = (
                        parentDepth: number,
                        childMenuItems: MappedLink[],
                        currentDepth: number,
                    ) => {
                        const cssOverrides = spreadProps("cssOverrides");
                        const maxDepth = otherProps.maxDepth || 3;
                        // XNG-Changes
                        const parentMenu = React.createRef<HTMLDivElement>();

                        return (
                            <FancyScroll refElement={parentMenu}>
                                <MenuStyle
                                    width={otherProps.width}
                                    ref={parentMenu}
                                    className="MenuStyle"
                                    data-depth={currentDepth}
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                >
                                    {childMenuItems
                                        ?.filter(item => !item.excludeFromNavigation)
                                        .map((item, index) => {
                                            /**
                                             * XNG-Changes
                                             */
                                            const maxDepth = item.maxDepth || parentDepth;

                                            const hasChildren =
                                                item.children?.some(o => !o.excludeFromNavigation) &&
                                                currentDepth < maxDepth;

                                            if (
                                                (item.type === "ChangeCustomerPage" &&
                                                    !this.props.displayChangeCustomerLink) ||
                                                !this.state.shouldRender
                                            ) {
                                                return null;
                                            }

                                            const menuItem = (
                                                <MenuItemStyle
                                                    width={otherProps.width}
                                                    css={applyStyledProp(
                                                        ["cssOverrides", "menuItem"],
                                                        resolvedMergeCss,
                                                    )}
                                                    key={`${item.title}_${index}`}
                                                    className="MenuItem"
                                                    data-test-selector="menuItem"
                                                    data-depth={currentDepth}
                                                    onClick={() => {
                                                        this.closeMenuWithoutChildren(hasChildren);
                                                    }}
                                                >
                                                    <Clickable
                                                        href={item.url}
                                                        onClick={() => {
                                                            item.onClick?.();
                                                            this.closeMenu();
                                                        }}
                                                        {...applyProp("menuItemClickableProps")}
                                                        {...item.clickableProps}
                                                    >
                                                        {item.iconProps && <IconMemo {...item.iconProps} />}
                                                        {item?.image && <LazyImage src={item.image} />}
                                                        <MenuItemText
                                                            hasChildren={hasChildren}
                                                            {...applyProp("menuItemTypographyProps")}
                                                            data-test-selector="menuItemText"
                                                        >
                                                            {item.title}
                                                        </MenuItemText>
                                                        {hasChildren && <IconMemo {...applyProp("moreIconProps")} />}
                                                    </Clickable>
                                                </MenuItemStyle>
                                            );
                                            return (
                                                <MenuItemWrapper
                                                    key={`${item.title}_${index}`}
                                                    className={`MenuItem ${
                                                        !hasChildren ? "no-child" : "has-child"
                                                    } depth-${currentDepth}`}
                                                >
                                                    {!hasChildren && menuItem}
                                                    {hasChildren && (
                                                        <LazyTippy
                                                            render={() =>
                                                                renderMenuItems(
                                                                    maxDepth,
                                                                    item!.children!,
                                                                    currentDepth + 1,
                                                                )
                                                            }
                                                            trigger="mouseenter"
                                                            placement="right-start"
                                                            interactive={true}
                                                            animation={false}
                                                            offset={[0, -1]}
                                                            delay={[100, 350]}
                                                            touch={item.url ? "hold" : true}
                                                            plugins={[hideOnPopperBlurPlugin]}
                                                            getReferenceClientRect={() => {
                                                                if (
                                                                    !parentMenu.current ||
                                                                    typeof parentMenu.current[
                                                                        "getBoundingClientRect"
                                                                    ] === "undefined"
                                                                ) {
                                                                    return new DOMRect(0, 0, 0, 0);
                                                                }
                                                                return parentMenu.current!.getBoundingClientRect();
                                                            }}
                                                            popperOptions={{
                                                                modifiers: [
                                                                    {
                                                                        name: "preventOverflow",
                                                                        options: { mainAxis: false },
                                                                    },
                                                                ],
                                                            }}
                                                        >
                                                            {menuItem}
                                                        </LazyTippy>
                                                    )}
                                                </MenuItemWrapper>
                                            );
                                        })}
                                </MenuStyle>
                            </FancyScroll>
                        );
                    };

                    const descriptionId = otherProps.descriptionId || uniqueId();
                    return (
                        <>
                            <LazyTippy
                                render={() => <>{renderMenuItems(otherProps.maxDepth || 0, menuItems, 1)}</>}
                                trigger={this.props.tippyTrigger}
                                placement="bottom-start"
                                hideOnClick={this.props.hideOnClick}
                                interactive={true}
                                animation={false}
                                delay={[10, 0]}
                                interactiveBorder={30}
                                offset={[0, 0]}
                                plugins={[hideOnPopperBlurPlugin]}
                                touch={["hold", 250]}
                                appendTo="parent"
                                onShow={instance => {
                                    hideAllTippyInstances();
                                    this.props.xHeaderUpdate({ key: "registerMenu", value: `${instance.id}_menu` });
                                    addScrollbarGutter();
                                    ariaAppHider.hide(document.body);
                                }}
                                onHidden={instance => {
                                    this.props.xHeaderUpdate({ key: "unregisterMenu", value: `${instance.id}_menu` });
                                    removeScrollbarGutter();
                                    ariaAppHider.show(document.body);
                                }}
                                popperOptions={{
                                    modifiers: [
                                        {
                                            name: "arrow",
                                            options: {
                                                padding: 0,
                                                element: ".menu-arrow",
                                            },
                                        },
                                    ],
                                }}
                            >
                                <MenuWrapper
                                    css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                                    {...{ "aria-labelledby": `${descriptionId}` }}
                                >
                                    {React.cloneElement(menuTrigger, {
                                        id: `${descriptionId}`,
                                        className: `trigger trigger-item`,
                                    })}
                                </MenuWrapper>
                            </LazyTippy>
                            <span ref={trigger} tabIndex={-1} />
                        </>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/** @component */
export default connect(null, mapDispatchToProps)(withTheme(Menu));

export { MenuItemText };
